import { supabase } from '../../../shared/services/supabase';

export interface DashboardSettings {
  id: string;
  location_id: string;
  overdue_ordered_yellow_days: number;
  overdue_ordered_red_days: number;
  stale_pending_yellow_days: number;
  stale_pending_red_days: number;
  receiving_backlog_days: number;
  usage_yellow_pct: number;
  usage_red_pct: number;
  ordering_day_of_week: number;
  enable_overdue_ordered: boolean;
  enable_stale_pending: boolean;
  enable_receiving_backlog: boolean;
  enable_usage_alert: boolean;
  created_at: string;
  updated_at: string;
}

export interface UpdateDashboardSettingsInput {
  location_id: string;
  overdue_ordered_yellow_days?: number;
  overdue_ordered_red_days?: number;
  stale_pending_yellow_days?: number;
  stale_pending_red_days?: number;
  receiving_backlog_days?: number;
  usage_yellow_pct?: number;
  usage_red_pct?: number;
  ordering_day_of_week?: number;
  enable_overdue_ordered?: boolean;
  enable_stale_pending?: boolean;
  enable_receiving_backlog?: boolean;
  enable_usage_alert?: boolean;
}

export const dashboardSettingsService = {
  /**
   * Get dashboard settings for a specific location
   */
  async getDashboardSettings(location_id: string): Promise<DashboardSettings> {
    const { data, error } = await supabase
      .from('location_dashboard_settings')
      .select('*')
      .eq('location_id', location_id)
      .single();

    if (error) {
      // If settings don't exist, create default settings
      if (error.code === 'PGRST116') {
        return this.createDashboardSettings(location_id);
      }
      console.error('Error fetching dashboard settings:', error);
      throw new Error(error.message);
    }

    return data as DashboardSettings;
  },

  /**
   * Get dashboard settings for multiple locations
   */
  async getDashboardSettingsForLocations(
    location_ids: string[]
  ): Promise<DashboardSettings[]> {
    const { data, error } = await supabase
      .from('location_dashboard_settings')
      .select('*')
      .in('location_id', location_ids);

    if (error) {
      console.error('Error fetching dashboard settings:', error);
      throw new Error(error.message);
    }

    return data as DashboardSettings[];
  },

  /**
   * Create default dashboard settings for a location
   */
  async createDashboardSettings(
    location_id: string
  ): Promise<DashboardSettings> {
    const { data, error } = await supabase
      .from('location_dashboard_settings')
      // @ts-expect-error - Database types not regenerated yet
      .insert({ location_id })
      .select()
      .single();

    if (error) {
      console.error('Error creating dashboard settings:', error);
      throw new Error(error.message);
    }

    return data as DashboardSettings;
  },

  /**
   * Update dashboard settings for a location
   */
  async updateDashboardSettings(
    input: UpdateDashboardSettingsInput
  ): Promise<DashboardSettings> {
    const updateData: Partial<DashboardSettings> = {};

    if (input.overdue_ordered_yellow_days !== undefined)
      updateData.overdue_ordered_yellow_days =
        input.overdue_ordered_yellow_days;
    if (input.overdue_ordered_red_days !== undefined)
      updateData.overdue_ordered_red_days = input.overdue_ordered_red_days;
    if (input.stale_pending_yellow_days !== undefined)
      updateData.stale_pending_yellow_days = input.stale_pending_yellow_days;
    if (input.stale_pending_red_days !== undefined)
      updateData.stale_pending_red_days = input.stale_pending_red_days;
    if (input.receiving_backlog_days !== undefined)
      updateData.receiving_backlog_days = input.receiving_backlog_days;
    if (input.usage_yellow_pct !== undefined)
      updateData.usage_yellow_pct = input.usage_yellow_pct;
    if (input.usage_red_pct !== undefined)
      updateData.usage_red_pct = input.usage_red_pct;
    if (input.ordering_day_of_week !== undefined)
      updateData.ordering_day_of_week = input.ordering_day_of_week;
    if (input.enable_overdue_ordered !== undefined)
      updateData.enable_overdue_ordered = input.enable_overdue_ordered;
    if (input.enable_stale_pending !== undefined)
      updateData.enable_stale_pending = input.enable_stale_pending;
    if (input.enable_receiving_backlog !== undefined)
      updateData.enable_receiving_backlog = input.enable_receiving_backlog;
    if (input.enable_usage_alert !== undefined)
      updateData.enable_usage_alert = input.enable_usage_alert;

    const { data, error } = await supabase
      .from('location_dashboard_settings')
      // @ts-expect-error - Supabase type mismatch
      .update(updateData)
      .eq('location_id', input.location_id)
      .select()
      .single();

    if (error) {
      console.error('Error updating dashboard settings:', error);
      throw new Error(error.message);
    }

    return data as DashboardSettings;
  },
};
